import { sanitizeHtml } from "./sanitize"
import { translateHtml } from "./translator"
import type { Language } from "./i18n"

// Shared cache for scheme HTML per slug+language to avoid refetch and retranslate
export const schemeCache = new Map<string, string>()

const cacheKey = (slug: string, lang: Language) => `${slug}:${lang}`

/**
 * Prefetch scheme HTML and store in shared cache. Returns cached or fetched HTML.
 * Optimizes navigation to render complete content immediately on route load.
 */
export async function prefetchScheme(slug: string, lang: Language): Promise<string | null> {
  try {
    const key = cacheKey(slug, lang)
    if (schemeCache.has(key)) return schemeCache.get(key) || null
    const url = `/schemes/${slug}/index.html`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Failed to load content (${res.status})`)
    const text = await res.text()
    const safe = sanitizeHtml(text)
    let final = lang === "hi" ? await translateHtml(safe, "hi") : lang === "pa" ? await translateHtml(safe, "pa") : safe

    // Override with provided high-fidelity Hindi content for NRLM when requested
    if (slug === "nrlm" && lang === "hi") {
      const nrlmHi = `
      <section>
        <h2>राष्ट्रीय ग्रामीण आजीविका मिशन (NRLM)/आजीविका</h2>
        <p>ग्रामीण विकास विभाग | हरियाणा</p>
        <p>प्रकाशन तिथि: 23 अक्टूबर, 2024</p>
        <p>वित्त पोषण पैटर्न : 60% केंद्र &amp; 40% राज्य</p>
        <p>भारत सरकार ने SGSY योजना के स्थान पर राष्ट्रीय ग्रामीण आजीविका मिशन (NRLM) प्रारंभ किया है। NRLM ग्रामीण क्षेत्रों में गरीबी में कमी के लिए एक प्रमुख कार्यक्रम है।</p>
        <p>हरियाणा राज्य में NRLM का कार्यान्वयन 01-04-2013 से किया गया है। प्रथम चरण में 4 जिलों अर्थात् भिवानी, कैथल, झज्जर और मेवात को गहन जिलों के रूप में चुना गया। प्रत्येक जिले में एक ब्लॉक को संसाधन ब्लॉक के रूप में लिया गया और NRLM का कार्यान्वयन 4 ब्लॉकों अर्थात् बवानी खेड़ा (भिवानी), सीवन (कैथल), मातनहैल (झज्जर) और तावडू (मेवात) में किया जा रहा है। रु. 2704.00 लाख की वार्षिक कार्य योजना को ग्रामीण विकास मंत्रालय (MoRD), भारत सरकार द्वारा मंज़ूरी दी गई।</p>
        <p>वर्ष 2014-15 में, 5 नए जिलों (पंचकूला, करनाल, रोहतक, हिसार और फतेहाबाद) को गहन कार्यान्वयन रणनीति में शामिल किया गया और इन जिलों में 10 ब्लॉकों (पिंजौर, मोरनी, घरौंडा, इंद्री, नरनौंद, हांसी-I, फतेहाबाद, झाकल, कलानौर और लखनमाजरा) का चयन किया गया।</p>
        <p>अधिक विवरण हेतु क्लिक करें: <a href="https://hsrlm.gov.in" target="_blank" rel="noopener"><code>http://hsrlm.gov.in</code></a></p>
      </section>
      `
      final = sanitizeHtml(nrlmHi)
    }

    // Punjabi override for NRLM when requested
    if (slug === "nrlm" && lang === "pa") {
      const nrlmPa = `
      <section>
        <h2>ਰਾਸ਼ਟਰੀ ਗ੍ਰਾਮੀਣ ਜੀਵਨਯਾਪਨ ਮਿਸ਼ਨ (NRLM)/ਆਜੀਵਿਕਾ</h2>
        <p>ਗ੍ਰਾਮੀਣ ਵਿਕਾਸ ਵਿਭਾਗ | ਹਰਿਆਣਾ</p>
        <p>ਪ੍ਰਕਾਸ਼ਨ ਤਾਰੀਖ: 23 ਅਕਤੂਬਰ, 2024</p>
        <p>ਵਿੱਤ ਪੈਟਰਨ : 60% ਕੇਂਦਰ &amp; 40% ਰਾਜ</p>
        <p>ਭਾਰਤ ਸਰਕਾਰ ਨੇ SGSY ਯੋਜਨਾ ਦੀ ਥਾਂ ਰਾਸ਼ਟਰੀ ਗ੍ਰਾਮੀਣ ਜੀਵਨਯਾਪਨ ਮਿਸ਼ਨ (NRLM) ਸ਼ੁਰੂ ਕੀਤਾ ਹੈ। NRLM ਪਿੰਡੂ ਖੇਤਰਾਂ ਵਿੱਚ ਗਰੀਬੀ ਘਟਾਉਣ ਲਈ ਇੱਕ ਫਲੈਗਸ਼ਿਪ ਪ੍ਰੋਗਰਾਮ ਹੈ।</p>
        <p>ਹਰਿਆਣਾ ਰਾਜ ਵਿੱਚ NRLM ਦਾ ਕਾਰਜਾਨਵਿਆਨ 01-04-2013 ਤੋਂ ਕੀਤਾ ਗਿਆ ਹੈ। ਪਹਿਲੇ ਚਰਨ ਵਿੱਚ 4 ਜ਼ਿਲ੍ਹੇ ਯਾਨੀ ਭਿਵਾਨੀ, ਕੈਥਲ, ਝੱਜਰ ਅਤੇ ਮੇਵਾਤ ਨੂੰ ਗਹਿਰੇ ਜ਼ਿਲ੍ਹਿਆਂ ਵਜੋਂ ਚੁਣਿਆ ਗਿਆ। ਹਰ ਜ਼ਿਲ੍ਹੇ ਵਿੱਚ ਇੱਕ ਬਲਾਕ ਨੂੰ ਸਰੋਤ ਬਲਾਕ ਵਜੋਂ ਲਿਆ ਗਿਆ ਅਤੇ NRLM ਦਾ ਕਾਰਜਾਨਵਿਆਨ 4 ਬਲਾਕਾਂ ਯਾਨੀ ਬਵਾਨੀ ਖੇੜਾ (ਭਿਵਾਨੀ), ਸੀਵਨ (ਕੈਥਲ), ਮਾਤਨਹੈਲ (ਝੱਜਰ) ਅਤੇ ਤਾਉਰੂ (ਮੇਵਾਤ) ਵਿੱਚ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ। ਰੁ. 2704.00 ਲੱਖ ਦੀ ਸਾਲਾਨਾ ਕਾਰਵਾਈ ਯੋਜਨਾ ਨੂੰ ਗ੍ਰਾਮੀਣ ਵਿਕਾਸ ਮੰਤਰਾਲੇ (MoRD), ਭਾਰਤ ਸਰਕਾਰ ਦੁਆਰਾ ਮਨਜ਼ੂਰ ਕੀਤਾ ਗਿਆ।</p>
        <p>2014-15 ਵਿੱਚ, 5 ਨਵੇਂ ਜ਼ਿਲ੍ਹੇ (ਪੰਚਕੂਲਾ, ਕਰਨਾਲ, ਰੋਹਤਕ, ਹਿਸਾਰ ਅਤੇ ਫਤਿਹਾਬਾਦ) ਨੂੰ ਗਹਿਰੇ ਕਾਰਜਾਨਵਿਅਨ ਰਣਨੀਤੀ ਵਿੱਚ ਸ਼ਾਮਲ ਕੀਤਾ ਗਿਆ ਅਤੇ ਇਨ੍ਹਾਂ ਜ਼ਿਲ੍ਹਿਆਂ ਵਿੱਚ 10 ਬਲਾਕ (ਪਿੰਜੌਰ, ਮੋਰਨੀ, ਘਰੌਂਡਾ, ਇੰਦਰੀ, ਨਰਨੌਂਦ, ਹਾਂਸੀ-I, ਫਤਿਹਾਬਾਦ, ਝਾਕਲ, ਕਲਾਨੌਰ ਅਤੇ ਲਖਨਮਾਜਰਾ) ਚੁਣੇ ਗਏ।</p>
        <p>ਵਧੇਰੇ ਵੇਰਵਿਆਂ ਲਈ ਕਲਿਕ ਕਰੋ: <a href="https://hsrlm.gov.in" target="_blank" rel="noopener"><code>http://hsrlm.gov.in</code></a></p>
      </section>
      `
      final = sanitizeHtml(nrlmPa)
    }
    schemeCache.set(key, final)
    return final
  } catch (e) {
    // Swallow errors here to avoid blocking navigation; detail page will handle alerts
    return null
  }
}

/**
 * Get cached scheme HTML if available.
 */
export function getCachedScheme(slug: string, lang: Language): string | null {
  return schemeCache.get(cacheKey(slug, lang)) || null
}